import { DateTime } from 'luxon'
import { UserDetail, Gender, UserUpdate, UserDataUpdate, LoginResponseLogin, PrivacyResponse, DateString, DateTimeString, ParentalControl } from './controllers/service-models.js'

export function parseToDate(str: DateString): Date | undefined {
  const date = DateTime.fromISO(str);
  if (date.isValid) {
    return date.toJSDate()
  }
  return undefined;
}

type IRaiSsoProfile = Omit<UserDetail, "birthDate" | "lastToken"> & {
  birthDate: Date
  lastToken?: Date;
}

type ToJson = ReturnType<RaiSsoProfile['toJSON']>
type GetMandatoryKeys<T> = {
  [P in keyof T]: T[P] extends Exclude<T[P], undefined> ? P : never
}[keyof T]
type RequiredFromJson = Pick<ToJson, GetMandatoryKeys<ToJson>>
type FromJson = Partial<ToJson> & RequiredFromJson

const MAX_ALLOWED_MILLIS = 100e6 * 24 * 60 * 60e3;

export class RaiSsoProfile implements IRaiSsoProfile {

//#region PRIVATE Fields
  private _firstName: string;
  private _lastName: string;
  private _gender: Gender;
  private _birthDate: Date;
  private _age: number;
  private _email: string;
  private _lid: string;
  private _photoURL?: string | null;
  private _personalizzazione?: boolean;
  private _privacyPolicyIsAccepted?: boolean;
  /**
   * usa la pubblicità profilata
   */
  private _pubblicitaProfilata?: boolean;
  /**
   * ha accettato il banner della pubblicità profilata
   */
  private _pubblicitaProfilataIsAccepted?: boolean;
  private _ua: string;
  private _uid: number;
  private _user_type: "persona" | "ente" = "persona";
  private _userDomain: { refreshToken: string } ;
  private _user_update?: UserUpdate[] | null;
  private _user_data_update?: UserDataUpdate | null;
  private _lastToken?: Date;
  private _parentalControl?: ParentalControl | null;
//#endregion

  constructor() {
    this._firstName = ''
    this._lastName = ''
    this._gender = 'A'
    this._birthDate = new Date(-1 * MAX_ALLOWED_MILLIS)
    this._age = 0
    this._email = ''
    this._lid = ''
    this._ua = ''
    this._uid = 0
    this._userDomain = { refreshToken: '' }
    this._expiration = Date.now() + 24 * 60 * 60 * 1000; // 1day
  }

//#region Public Getters
  public get firstName() { return this._firstName }
  public get lastName() { return this._lastName }

  public get nickname() { return `${this.firstName} ${this.lastName}`; }

  public get gender() { return this._gender }
  public get birthDate() { return this._birthDate }

  private get birthDateTime() { return DateTime.fromJSDate(this.birthDate); }

  public get birthDay() { return this.birthDate && this.birthDateTime.isValid ? this.birthDateTime.day : NaN; }
  public get birthMonth() { return this.birthDate && this.birthDateTime.isValid ? this.birthDateTime.month : NaN; }
  public get birthYear() { return this.birthDate && this.birthDateTime.isValid ? this.birthDateTime.year : NaN; }

  public get age() { return this._age }
  public get email() { return this._email }
  public get lid() { return this._lid }
  public get photoURL() { return this._photoURL }
  public get personalizzazione() { return this._personalizzazione }
  public get privacyPolicyIsAccepted() { return this._privacyPolicyIsAccepted }
  public get pubblicitaProfilata() { return this._pubblicitaProfilata }
  public get pubblicitaProfilataIsAccepted() { return this._pubblicitaProfilataIsAccepted }
  public get social() { return this._social }
  public get ua() { return this._ua }
  public get uid() { return this._uid }
  public get user_type() { return this._user_type }
  public get userDomain() { return this._userDomain }
  public get user_update() { return this._user_update }
  public get user_data_update() { return this._user_data_update }
  public get lastToken() { return this._lastToken }
  public get parental_control() { return this._parentalControl }
//#endregion

  private _social?: {
    loginProvider: string;
    photoURL?: string | null;
    thumbnailURL?: string | null;
  };

  public get isSocial() {
    return this._social?.loginProvider != null;
  }

  private _expiration: number = MAX_ALLOWED_MILLIS;

  public get expirationDate() { return new Date(this._expiration); }

  public static fromLogin(data: Pick<LoginResponseLogin, 'raisso' | 'ente_accreditato' | 'social' | 'user_data_update' | 'user_update'>) {
    const profile = new RaiSsoProfile()
    const { raisso, ente_accreditato, social, user_update, user_data_update } = data
    if (raisso) {
      profile._age = raisso.age
      profile._birthDate = DateTime.fromISO(raisso.birthDate).toJSDate()
      profile._email = raisso.email
      profile._lid = raisso.lid
      profile._firstName = raisso.firstName
      profile._gender = raisso.gender
      profile._lastName = raisso.lastName
      profile._personalizzazione = raisso.personalizzazione
      profile._photoURL = raisso.photoURL ?? social?.photoURL ?? social?.thumbnailURL
      profile._privacyPolicyIsAccepted = raisso.privacy_Policy_isAccepted
      profile._pubblicitaProfilata = raisso.pubblicitaProfilata
      profile._pubblicitaProfilataIsAccepted = raisso.privacy_Pubblicita_isAccepted
      if (social) { profile._social = social }
      profile._ua = raisso.ua
      profile._uid = raisso.uid
      profile._userDomain = raisso.userDomain
      profile._user_data_update = user_data_update ?? raisso.user_data_update
      profile._user_update = user_update ?? raisso.user_update
      profile._user_type = ente_accreditato ? 'ente' : 'persona'
      profile._lastToken = raisso.lastToken ? DateTime.fromISO(raisso.lastToken).toJSDate() : undefined;
      profile._parentalControl = raisso.parental_control;
    }
    return profile
  }

  public renewExpiration(exp: number) {
    if (exp >= -MAX_ALLOWED_MILLIS && exp <= MAX_ALLOWED_MILLIS) {
      this._expiration = exp;
    } else {
      this._expiration = Date.now() + 24 * 60 * 60 * 1000;
    }
  }

  public setPrivacyData(data: Pick<PrivacyResponse, 'personalizzazione' | 'privacy_Policy_isAccepted' | 'pubblicitaProfilata' | 'privacy_Pubblicita_isAccepted'>) {
    this._personalizzazione = data.personalizzazione;
    this._privacyPolicyIsAccepted = data.privacy_Policy_isAccepted;
    this._pubblicitaProfilata = data.pubblicitaProfilata;
    this._pubblicitaProfilataIsAccepted = data.privacy_Pubblicita_isAccepted
  }

  public toJSON() {
    const { age, birthDate, email, lid, _expiration, firstName, gender, lastName, personalizzazione, photoURL,
      privacyPolicyIsAccepted, pubblicitaProfilata, pubblicitaProfilataIsAccepted, ua, uid, userDomain, user_data_update,
      user_update, user_type, _social, lastToken, parental_control } = this;
    const birthDateTime = DateTime.fromJSDate(birthDate);
    const lastTokenTime = lastToken ? DateTime.fromJSDate(lastToken) : undefined;
    return {
      age,
      birthDate: birthDateTime.isValid ? birthDateTime.toFormat('yyyy-MM-dd') : null,
      email,
      lid,
      expiration: _expiration,
      firstName,
      gender,
      lastName,
      personalizzazione,
      photoURL,
      privacyPolicyIsAccepted,
      pubblicitaProfilata,
      pubblicitaProfilataIsAccepted,
      ua,
      uid,
      userDomain,
      user_data_update,
      user_type,
      user_update,
      social: _social,
      lastToken: lastTokenTime?.isValid ? lastTokenTime.toFormat('yyyy-MM-dd') : undefined,
      parental_control,
    };
  }

  public static fromJSON(json: FromJson) {
    const { age, birthDate, email, lid, expiration, firstName, gender, lastName, personalizzazione, photoURL,
      privacyPolicyIsAccepted, pubblicitaProfilataIsAccepted, pubblicitaProfilata, ua, user_update,
      uid, user_type, social, userDomain, user_data_update, lastToken, parental_control } = json;

    const p = this.fromLogin({
      ente_accreditato: user_type === 'ente',
      social,
      raisso: {
        age,
        birthDate: (birthDate ?? '') as DateString,
        email,
        lid,
        firstName,
        gender,
        lastName,
        ua,
        uid,
        userDomain,
        personalizzazione,
        photoURL,
        privacy_Policy_isAccepted: privacyPolicyIsAccepted,
        privacy_Pubblicita_isAccepted: pubblicitaProfilataIsAccepted,
        pubblicitaProfilata,
        user_data_update,
        user_update,
        lastToken: lastToken as DateTimeString,
        parental_control,
      }
    })
    p.renewExpiration(expiration)
    return p;
  }
}

type StringNumber = string;

export interface GigyaUser {
  firstName: string
  lastName: string
  nickname: string
  age: StringNumber
  birthDay: StringNumber
  birthMonth: StringNumber
  birthYear: StringNumber
  email: string
  gender: 'm' | 'f' | 'u'
  isSiteUID: boolean
  photoURL: string
  thumbnailURL?: string | null
  UID: string
  UIDSignature: string
  signatureTimestamp: StringNumber
  loginProvider: string
  identities: Record<string, Record<string, string | number | boolean>>
}

export interface GigyaLoginCallback {
  errorCode: number,
  errorMessage: string,
  user: GigyaUser,
  UID: string,
  UIDSignature: string,
  signatureTimestamp: StringNumber
}
